import { useState, type PropsWithChildren } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BRAND, kstYear } from '@tarot/shared';
import { BrandLogo } from '../brand/BrandLogo';
import { useLogout, useMe } from '../features/auth/useMe';
import { INFO_MENU } from '../features/info/InfoPage';
import { CaretIcon, CardsIcon, HomeIcon, LogoutIcon, PersonIcon } from './icons';
import { isToss } from '../lib/platform';

/**
 * 모바일 우선 레이아웃. PC에서는 max-w 컨테이너로 동일 UI. 푸터 구분선은 화면 끝까지.
 * iOS 홈 화면 PWA는 `viewport-fit=cover` + status-bar-style=black-translucent라
 * 본문이 화면 맨 위(y=0)부터 그려진다 → safe-area 인셋을 직접 비워 주지 않으면
 * 헤더가 노치·시계·배터리 표시와 겹친다. 가로 모드의 노치 대비로 좌우도 함께 준다.
 */
export function AppShell({ children }: PropsWithChildren) {
  return (
    <div className="flex min-h-dvh flex-col pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] pt-[env(safe-area-inset-top)]">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col px-5">
        {/*
          토스 미니앱에는 토스가 그리는 상단 바(앱 이름·뒤로가기)가 이미 얹힌다. 그 위에 우리
          헤더까지 두면 화면 위쪽에 머리가 둘 달린 꼴이라 **번들 심사에서 반려된다**
          (2026-09-10 실제 반려 사유, doc/apps-in-toss-setup.md §4-9).
          로고를 빼는 대신 홈으로 가는 길은 메뉴 안에 넣었다 — 그냥 없애면 갈 곳이 사라진다.
        */}
        <header className={`flex items-center py-5 ${isToss ? 'justify-end' : 'justify-between'}`}>
          {!isToss && (
            <Link to="/" aria-label={`${BRAND.nameKo} 홈`}>
              <BrandLogo />
            </Link>
          )}
          <HeaderMenu />
        </header>
        <main className="flex flex-1 flex-col">{children}</main>
      </div>
      <Footer />
    </div>
  );
}

/* ── 헤더 우측: 통합 메뉴 (프로필/로그인 + 지난 타로) ── */

function HeaderMenu() {
  const { me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="메뉴 열기"
        className="flex items-center gap-1.5 rounded-full border border-gold/30 bg-white/5 px-3 py-1.5 transition hover:border-gold/60 hover:bg-gold/10"
      >
        {me ? (
          <span className="max-w-20 truncate text-xs font-medium text-gold">{me.nickname}</span>
        ) : (
          <PersonIcon className="h-4 w-4 text-gold" />
        )}
        <CaretIcon className={`h-3 w-3 text-moon-dim transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={close} aria-hidden />
          <div className="absolute right-0 top-full z-50 mt-2 w-44 overflow-hidden rounded-2xl border border-white/10 bg-night-2 py-1.5 shadow-xl shadow-black/60">
            {me && (
              <p className="border-b border-white/10 px-4 pb-2 pt-1 text-xs text-moon-dim">
                <span className="font-medium text-gold">{me.nickname}</span>님
              </p>
            )}
            {/* 미니앱에는 로고(홈 링크)가 없다 — 홈으로 가는 길을 여기서 대신한다 */}
            {isToss && (
              <MenuItem
                onClick={() => {
                  close();
                  navigate('/');
                }}
              >
                <HomeIcon className="h-4 w-4 text-gold/80" /> 홈
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                close();
                navigate('/history');
              }}
            >
              <CardsIcon className="h-4 w-4 text-gold/80" /> 지난 타로
            </MenuItem>
            {me ? (
              <>
                <MenuItem
                  onClick={() => {
                    close();
                    navigate('/profile');
                  }}
                >
                  <PersonIcon className="h-4 w-4 text-gold/80" /> 내 정보
                </MenuItem>
                <MenuItem
                  onClick={async () => {
                    close();
                    await logout();
                    navigate('/');
                  }}
                >
                  <LogoutIcon className="h-4 w-4 text-gold/80" /> 로그아웃
                </MenuItem>
              </>
            ) : (
              <MenuItem
                onClick={() => {
                  close();
                  navigate('/login');
                }}
              >
                <PersonIcon className="h-4 w-4 text-gold/80" /> 로그인
              </MenuItem>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MenuItem({ children, onClick }: PropsWithChildren<{ onClick: () => void }>) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm transition hover:bg-gold/10"
    >
      {children}
    </button>
  );
}

/* ── 푸터: 구분선 + 서비스 메뉴 + 위험성 안내 + 저작권 ── */

function Footer() {
  return (
    // 구분선(border-t)은 화면 양옆 끝까지 — 내용만 max-w 컨테이너로
    <footer className="mt-10 w-full border-t border-white/10">
      <div className="mx-auto flex w-full max-w-md flex-col gap-4 px-5 py-6">
        <nav className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-moon-dim">
          {/* 카드 도감은 검색 유입의 착지 페이지 — 내부 링크로도 노출해 색인을 돕는다 */}
          <Link to="/cards" className="transition hover:text-moon">
            카드 도감
          </Link>
          <Link to="/glossary" className="transition hover:text-moon">
            타로 용어 사전
          </Link>
          {INFO_MENU.map((m) => (
            <Link key={m.slug} to={`/info/${m.slug}`} className="transition hover:text-moon">
              {m.label}
            </Link>
          ))}
        </nav>
        <p className="text-xs leading-relaxed text-moon-dim/70">{BRAND.disclaimer}</p>
        <p className="text-[10px] text-moon-dim/50">
          © {kstYear()} {BRAND.nameKo} ({BRAND.nameEn}) · 카드 이미지: RWS Tarot (1909), Public
          Domain
        </p>
      </div>
    </footer>
  );
}
